import {
  DocumentSnapshot,
  QueryConstraint,
  QuerySnapshot,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  ICreateExamResource,
  IExamResource,
  IExamResourceRaw,
  IGenericError,
} from '../types'

const examResourcesCollectionName = 'examResources'

const examResourcesCollectionRef = collection(
  firebaseDb,
  examResourcesCollectionName,
)

const examResourcesDocRef = (id: string) =>
  doc(firebaseDb, examResourcesCollectionName, id)

export const getExamResourceById = async (
  id: string,
): Promise<IExamResource | undefined> => {
  try {
    const docRef = examResourcesDocRef(id)
    const docSnap = (await getDoc(
      docRef,
    )) as unknown as DocumentSnapshot<IExamResourceRaw>

    const dacData = docSnap.data()

    if (!dacData) {
      return undefined
    }

    const doc: IExamResource = {
      id: docSnap.id,
      ...dacData,
    }

    return doc
  } catch (error: unknown) {
    console.error(`error while tying to get examResource by id: ${id} :>> `, {
      message: (error as IGenericError)?.message,
      error,
    })
  }
}

export async function getExamResources(
  queryConstraints: QueryConstraint[] = [],
): Promise<IExamResource[]> {
  try {
    let q = query(examResourcesCollectionRef, ...queryConstraints)

    const querySnapshot = (await getDocs(q)) as QuerySnapshot<IExamResourceRaw>

    let examResources: IExamResource[] = []

    querySnapshot.forEach((doc) => {
      examResources.push({
        id: doc.id,
        ...doc.data(),
      })
    })

    return examResources
  } catch (error: unknown) {
    console.error(`error while trying to get examResources :>> `, {
      message: (error as IGenericError)?.message,
      error,
      queryConstraints,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get examResources`,
    )
  }
}

export async function getExamResourcesListByIds(
  ids: string[],
): Promise<IExamResource[]> {
  try {
    const questions: IExamResource[] = await Promise.all(
      ids.map(async (id) => {
        return await getExamResourceById(id)
      }),
    ).then((data) =>
      // filter out undefined values
      data.filter((q): q is IExamResource => q !== undefined),
    )

    return questions
  } catch (error: unknown) {
    console.error(`error while trying to get examResources list :>> `, {
      message: (error as IGenericError)?.message,
      error,
      ids,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get examResources list.`,
    )
  }
}

export async function setExamResourceById(
  id: string,
  resource: ICreateExamResource,
): Promise<void> {
  try {
    await setDoc(examResourcesDocRef(id), {
      ...resource,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    console.error(
      `error while trying to set exam resource by id: ${id} :>> `,
      error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to set exam resource by id`,
    )
  }
}
